import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

import { useAuth } from './hooks/use-auth';
import { getTraceContext } from '@/shared/utils/trace-context';
import { matchesPathPrefixes } from './utils';
import { isE2ETestMode } from './utils/e2e';

const API_V1_PATH_PREFIXES = ['/api/v1'];

export function AxiosInterceptorProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    getAccessTokenSilently,
    logout,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useAuth();
  const [isReady, setIsReady] = useState(false);
  // use ref to store the interceptor id
  const interceptorIdRef = useRef<number | undefined>(undefined);

  // use refs to store latest auth values to avoid stale closures in interceptor
  const isAuthenticatedRef = useRef(isAuthenticated);
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  const logoutRef = useRef(logout);

  // Update refs whenever values change
  // eslint-disable-next-line no-restricted-syntax -- reason: Update refs to avoid stale closures in axios interceptors
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
    getAccessTokenSilentlyRef.current = getAccessTokenSilently;
    logoutRef.current = logout;
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Setup axios interceptor requires imperative API initialization
  useEffect(() => {
    // If interceptor already exists (React StrictMode double-render), skip
    if (interceptorIdRef.current !== undefined) {
      setIsReady(true);
      return;
    }

    const setupInterceptor = () => {
      // Check for E2E test mode (SSR-safe: checks build-time VITE_E2E_TEST_MODE first,
      // then runtime window.__ENV__ if available)
      const isE2EMode = isE2ETestMode();

      // Setup interceptor - read from refs to avoid stale closure
      const requestInterceptor = axios.interceptors.request.use(
        async config => {
          // E2E test mode - add test header and skip auth
          if (isE2EMode) {
            config.headers['X-E2E-Test-Mode'] = 'true';
            return config;
          }

          // Read latest values from refs instead of closure
          const currentIsAuthenticated = isAuthenticatedRef.current;
          const currentGetAccessTokenSilently =
            getAccessTokenSilentlyRef.current;
          const currentLogout = logoutRef.current;

          // Production mode - add request ID header and auth token for API requests
          const traceContext = getTraceContext();
          config.headers['x-kog-request-id'] = traceContext.requestId;

          // Only try to get token if authenticated
          if (currentIsAuthenticated) {
            try {
              // Always try to get token - Auth0 will handle auth state internally
              const token = await currentGetAccessTokenSilently();
              config.headers.Authorization = `Bearer ${token}`;
            } catch (error) {
              if (error instanceof Error) {
                if (error.message.includes('Missing Refresh Token')) {
                  currentLogout();
                }
              }
              // Reject the request if we can't get the token
              return Promise.reject(
                new Error('Failed to get access token for API request'),
              );
            }
          }

          return config;
        },
        error => Promise.reject(error),
        {
          runWhen: config => matchesPathPrefixes(config, API_V1_PATH_PREFIXES),
        },
      );

      return requestInterceptor;
    };

    interceptorIdRef.current = setupInterceptor();
    setIsReady(true);
  }, []);

  if (isAuthLoading || !isReady) {
    return null;
  }

  return <>{children}</>;
}
